const STORAGE_KEY = 'nm.mobileSwapSession'
const MAX_AGE_MS = 86_400_000
/** Tras este tiempo sin hash, la fase wallet/submitted ya no reanuda ni bloquea rutas. */
export const MOBILE_SWAP_PENDING_MAX_AGE_MS = 15 * 60_000

export const MOBILE_SWAP_CONFIRMED_EVENT = 'nm:mobile-swap-confirmed'
export const MOBILE_SWAP_SESSION_EVENT = 'nm:mobile-swap-session'

export type MobileSwapSessionPhase = 'review' | 'wallet' | 'submitted' | 'confirmed' | 'failed'

export type MobileSwapSession = {
  chainId: number
  account: string
  hash?: string
  summary?: string
  inputCurrencyId?: string
  outputCurrencyId?: string
  inputSymbol: string
  outputSymbol: string
  inputAmount: string
  outputAmount: string
  phase: MobileSwapSessionPhase
  dismissed?: boolean
  updatedAt: number
}

export type MobileSimpleScreen = 'convert' | 'review' | 'success'

export function resolveMobileSwapScreen(
  session: MobileSwapSession | null,
  opts: {
    txHash?: string
    attemptingTxn?: boolean
    showConfirm?: boolean
    confirmed?: boolean
    recovering?: boolean
  } = {},
): MobileSimpleScreen {
  const { txHash, attemptingTxn = false, showConfirm = false, confirmed = false, recovering = false } = opts

  if (confirmed) return 'success'
  if (session?.phase === 'confirmed' && session.hash) return 'success'
  if (txHash && !attemptingTxn && !showConfirm) return 'success'
  if (recovering || showConfirm || attemptingTxn) return 'review'
  if (
    session &&
    (session.phase === 'review' ||
      session.phase === 'wallet' ||
      session.phase === 'submitted' ||
      (session.hash && session.phase !== 'failed' && session.phase !== 'confirmed'))
  ) {
    return 'review'
  }
  return 'convert'
}

function readRaw(): MobileSwapSession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as MobileSwapSession
    if (!parsed?.account || !parsed?.chainId) return null
    return parsed
  } catch {
    return null
  }
}

export function readMobileSwapSession(): MobileSwapSession | null {
  const session = readRaw()
  if (!session) return null
  if (session.dismissed) return null
  if (Date.now() - session.updatedAt > MAX_AGE_MS) {
    clearMobileSwapSession()
    return null
  }
  return session
}

export function writeMobileSwapSession(session: MobileSwapSession): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch {
    /* quota / private mode */
  }
}

export function patchMobileSwapSession(patch: Partial<MobileSwapSession>): MobileSwapSession | null {
  const current = readRaw()
  if (!current) return null
  const next: MobileSwapSession = {
    ...current,
    ...patch,
    updatedAt: Date.now(),
  }
  writeMobileSwapSession(next)
  return next
}

export function clearMobileSwapSession(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

export function dismissMobileSwapSession(): void {
  patchMobileSwapSession({ dismissed: true })
}

/** Tras este tiempo sin hash en fase wallet, la sesión se considera abandonada al reentrar. */
export const MOBILE_SWAP_WALLET_ABANDON_MS = 2 * 60_000

/** Sesión wallet/submitted sin hash tras volver de MetaMask — evita reanudar indefinidamente. */
export function isStaleMobileSwapPendingSession(
  session: MobileSwapSession,
  now = Date.now(),
): boolean {
  if (session.hash) return false
  if (session.phase !== 'wallet' && session.phase !== 'submitted') return false
  return now - session.updatedAt > MOBILE_SWAP_PENDING_MAX_AGE_MS
}

/** Firma nunca llegó (MetaMask cerrado): no reanudar pantalla de espera al recargar. */
export function isAbandonedMobileSwapWalletSession(
  session: MobileSwapSession,
  now = Date.now(),
): boolean {
  if (session.hash) return false
  if (session.phase !== 'wallet') return false
  return now - session.updatedAt > MOBILE_SWAP_WALLET_ABANDON_MS
}

/** Espera activa de firma en MetaMask (sin tx enviada). */
export function isMobileSwapWalletWaitActive(
  session: MobileSwapSession | null = readMobileSwapSession(),
): boolean {
  if (!session || session.dismissed) return false
  if (session.hash) return false
  if (session.phase !== 'wallet') return false
  if (isStaleMobileSwapPendingSession(session)) return false
  return true
}

/** Swap finalizado: elimina sesión para no reanudar /swap ni disparar guards móviles. */
export function completeMobileSwapSession(): void {
  clearMobileSwapSession()
}

const DISMISSED_HASHES_KEY = 'nm.mobileSwapDismissedHashes'
const DISMISSED_HASHES_MAX = 32

function readDismissedSwapHashes(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_HASHES_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.map(String) : []
  } catch {
    return []
  }
}

/** Tras "Nuevo swap": evita re-mostrar éxito por polling/receipt del swap ya cerrado. */
export function recordDismissedMobileSwapHash(hash: string): void {
  if (typeof window === 'undefined' || !hash) return
  const normalized = hash.toLowerCase()
  const existing = readDismissedSwapHashes()
  if (existing.includes(normalized)) return
  try {
    window.sessionStorage.setItem(
      DISMISSED_HASHES_KEY,
      JSON.stringify([normalized, ...existing].slice(0, DISMISSED_HASHES_MAX)),
    )
  } catch {
    /* quota / private mode */
  }
}

export function isDismissedMobileSwapHash(hash: string | undefined): boolean {
  if (!hash) return false
  return readDismissedSwapHashes().includes(hash.toLowerCase())
}

export function getDismissedMobileSwapHashes(): string[] {
  return readDismissedSwapHashes()
}

/** Usuario rechazó en MetaMask o abandonó firma: no reanudar espera ni recuperar hash antiguo. */
export function abortMobileSwapWalletSession(): void {
  const session = readRaw()
  if (!session) return
  if (session.hash) return
  if (session.phase !== 'wallet' && session.phase !== 'submitted') return
  clearMobileSwapSession()
}

export function buildSwapSessionSummary(input: {
  inputAmount: string
  inputSymbol: string
  outputAmount: string
  outputSymbol: string
}): string {
  return `Swap ${input.inputAmount} ${input.inputSymbol} for ${input.outputAmount} ${input.outputSymbol}`
}
